import React, { FC, useEffect, useRef } from 'react';
import ChangeProductIcon from 'src/components/pages/userInterfaceSettings/icons/change-product.svg';
import { T } from 'src/shared/helpers';
import ExtensionIcon from 'src/shared/material_icons/action/extension.svg';
import HomeIcon from 'src/shared/material_icons/action/home.svg';
import FolderIcon from 'src/shared/material_icons/file/folder.svg';
import MagicIcon from 'src/shared/material_icons/image/auto_fix_high.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import LogoZakeke from 'src/shared/svg/logoZakeke';
import styled from 'styled-components';
import { Icon } from '../../../../widgets/icon';
import { ZTooltip } from '../../../../widgets/ztooltip';
import { AICustomApp } from '../../customApps/customAppTypes';
import { ToolConfig, ToolType } from '../../types';
import { Carousel } from '../presets/carousel';

const LogoWrapper = styled.div`
    position: absolute;
    left: 24px;
    display: flex;
    align-items: center;

    svg {
        height: 24px;
        width: auto;
    }
`;

const HeaderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 12px 24px;
    background: ${(props) => props.theme.app.backgroundColorBase};
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    min-height: 64px;
`;

const ToolsCarouselWrapper = styled(Carousel)`
    max-width: calc(100% - 780px);
`;

const ToolButton = styled.button<{ $active: boolean; $disabled?: boolean }>`
    padding: 10px 20px;
    border: none;
    border-radius: 18px;
    background: ${(props) => (props.$active ? '#000000' : 'transparent')};
    color: ${(props) => (props.$active ? '#ffffff' : '#000000')};
    font-size: 13px;
    font-weight: 500;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
    transition:
        background 0.2s ease,
        color 0.2s ease,
        opacity 0.2s ease;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
        background: ${(props) => (props.$disabled ? (props.$active ? '#000000' : 'transparent') : props.$active ? '#000000' : '#e5e7eb')};
    }
`;

const CloseButton = styled.button`
    position: absolute;
    right: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #000000;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: #333333;
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        min-width: 0;
        fill: currentColor;
    }
`;

const HeaderActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: none;
    border-radius: 9999px;
    background: #f3f5f6;
    color: #000000;
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease;

    &:hover {
        background: #f97316;
        color: white;
    }
`;

const HeaderIconButton = styled(HeaderActionButton)`
    width: 36px;
    height: 36px;
    padding: 0;

    ${Icon} {
        width: 22px;
        height: 22px;
        fill: currentColor;
        min-width: 0;
        min-height: 0;
    }
`;

const CustomizerActions = styled.div`
    position: absolute;
    left: 150px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const LeftSwitchWrapper = styled.div`
    position: absolute;
    left: 150px;
    display: flex;
    align-items: center;
    background: #f3f5f6;
    border-radius: 9999px;
    padding: 4px;
`;

const LeftSwitchSlider = styled.div<{ $position: 'left' | 'right' }>`
    position: absolute;
    top: 4px;
    left: ${(props) => (props.$position === 'left' ? '4px' : '50%')};
    width: calc(50% - 4px);
    height: calc(100% - 8px);
    background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    border-radius: 9999px;
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 0;
`;

const HomeButton = styled(HeaderIconButton)<{ $disabled?: boolean }>`
    position: absolute;
    left: 360px;
    opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    transition:
        background 0.2s ease,
        color 0.2s ease,
        opacity 0.2s ease;
`;

const ManageCustomAppsButton = styled(HeaderIconButton)<{ $disabled?: boolean }>`
    position: absolute;
    right: 76px;
    opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
`;

const LeftSwitchOption = styled.button<{ $active: boolean; $disabled?: boolean }>`
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    padding: 5px 12px;
    border: none;
    border-radius: 9999px;
    background: transparent;
    color: ${(props) => (props.$active ? '#ffffff' : '#000000')};
    font-size: 13px;
    font-weight: 500;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
    transition:
        color 0.3s ease,
        opacity 0.2s ease;
    white-space: nowrap;

    &:hover {
        color: ${(props) => (props.$active ? '#ffffff' : '#374151')};
    }

    > * {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }
`;

interface LandingTab {
    id: string;
    name: string;
    active: boolean;
    onClick: () => void;
    disabledTooltip?: string;
}

interface HeaderProps {
    tools: ToolConfig[];
    customApps?: readonly AICustomApp[];
    /** Selected tool ID, or null for homepage mode */
    selectedTool: ToolType | null;
    selectedCustomAppId?: number | null;
    onToolChange: (toolId: ToolType) => void;
    onCustomAppChange?: (app: AICustomApp) => void;
    onManageCustomApps?: () => void;
    onClose?: () => void;
    onGoHome?: () => void;
    onShowLibrary?: () => void;
    isLibraryActive?: boolean;
    isIframeMode?: boolean;
    onSwitchProduct?: () => void;
    /** Whether navigation is disabled (e.g. during auto-capture) */
    isNavigationDisabled?: boolean;
    /** Tabs to show in the header when a homepage landing overlay is open */
    landingTabs?: LandingTab[];
    disabledTooltipByToolId?: Record<string, string | undefined>;
}

export const Header: FC<HeaderProps> = ({
    tools,
    customApps = [],
    selectedTool,
    selectedCustomAppId,
    onToolChange,
    onCustomAppChange,
    onManageCustomApps,
    onClose,
    onGoHome,
    onShowLibrary,
    isLibraryActive,
    isIframeMode,
    onSwitchProduct,
    isNavigationDisabled = false,
    landingTabs,
    disabledTooltipByToolId
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const isHomepage = selectedTool === null && !selectedCustomAppId;
    const activeLandingTabs = landingTabs ?? [];
    const shouldShowLandingTabs = isHomepage && activeLandingTabs.length > 0;
    const shouldRenderTabs = !isIframeMode && !isLibraryActive;

    // Scroll the active tab into view when it changes — works for both
    // workspace mode (selectedTool) and homepage overlay mode (landingTabs).
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        let activeIndex = -1;
        if (selectedTool) {
            activeIndex = tools.findIndex((t) => t.id === selectedTool);
        } else if (selectedCustomAppId) {
            const customIndex = customApps.findIndex((app) => app.aiCustomAppID === selectedCustomAppId);
            activeIndex = customIndex >= 0 ? tools.length + customIndex : -1;
        } else if (activeLandingTabs.length > 0) {
            activeIndex = activeLandingTabs.findIndex((t) => t.active);
        }
        if (activeIndex < 0) return;

        const buttons = container.querySelectorAll('button');
        const activeButton = buttons[activeIndex];

        if (activeButton) {
            activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }, [selectedTool, selectedCustomAppId, tools, customApps, landingTabs]);

    const handleToolsClick = () => {
        if (isNavigationDisabled) return;
        if (isLibraryActive && onShowLibrary) {
            onShowLibrary();
        }
    };

    const handleLibraryClick = () => {
        if (isNavigationDisabled) return;
        if (!isLibraryActive && onShowLibrary) {
            onShowLibrary();
        }
    };

    return (
        <HeaderContainer>
            <LogoWrapper>
                <LogoZakeke />
            </LogoWrapper>
            {isIframeMode && (
                <CustomizerActions>
                    <ZTooltip position='bottom' title={T._('Switch product', 'Admin')} enterDelay={0} exitDelay={0} interactive={false}>
                        <HeaderIconButton onClick={onSwitchProduct}>
                            <Icon>
                                <ChangeProductIcon />
                            </Icon>
                        </HeaderIconButton>
                    </ZTooltip>
                </CustomizerActions>
            )}
            {!isIframeMode && onShowLibrary && (
                <LeftSwitchWrapper>
                    <LeftSwitchSlider $position={isLibraryActive ? 'right' : 'left'} />
                    <LeftSwitchOption $active={!isLibraryActive} $disabled={isNavigationDisabled} onClick={handleToolsClick}>
                        <Icon>
                            <MagicIcon />
                        </Icon>
                        {T._('Tools', 'Admin')}
                    </LeftSwitchOption>
                    <LeftSwitchOption $active={!!isLibraryActive} $disabled={isNavigationDisabled} onClick={handleLibraryClick}>
                        <Icon>
                            <FolderIcon />
                        </Icon>
                        {T._('Library', 'Admin')}
                    </LeftSwitchOption>
                </LeftSwitchWrapper>
            )}
            {!isIframeMode && !isLibraryActive && onGoHome && (!isHomepage || activeLandingTabs.length > 0) && (
                <HomeButton
                    $disabled={isNavigationDisabled}
                    onClick={isNavigationDisabled ? undefined : onGoHome}
                    aria-label={T._('Home', 'Admin')}
                    title={T._('Return to tools list', 'Admin')}
                >
                    <Icon>
                        <HomeIcon />
                    </Icon>
                </HomeButton>
            )}
            {shouldRenderTabs && (
                <ToolsCarouselWrapper scrollRef={scrollRef} scrollAmount={150}>
                    {shouldShowLandingTabs
                        ? activeLandingTabs.map((tab) => {
                              const isDisabled = !!tab.disabledTooltip;
                              const button = (
                                  <ToolButton key={tab.id} $active={tab.active} $disabled={isDisabled} onClick={isDisabled ? undefined : tab.onClick}>
                                      {T._(tab.name, 'Admin')}
                                  </ToolButton>
                              );

                              return tab.disabledTooltip ? (
                                  <ZTooltip key={tab.id} title={tab.disabledTooltip} position='bottom' addContainer>
                                      {button}
                                  </ZTooltip>
                              ) : (
                                  button
                              );
                          })
                        : tools.map((tool) => {
                              const disabledTooltip = disabledTooltipByToolId?.[tool.id];
                              const isDisabled = isNavigationDisabled || !!disabledTooltip;
                              const button = (
                                  <ToolButton
                                      key={tool.id}
                                      $active={selectedTool === tool.id}
                                      $disabled={isDisabled}
                                      onClick={isDisabled ? undefined : () => onToolChange(tool.id)}
                                  >
                                      {T._(tool.name, 'Admin')}
                                  </ToolButton>
                              );

                              return disabledTooltip ? (
                                  <ZTooltip key={tool.id} title={disabledTooltip} position='bottom' addContainer>
                                      {button}
                                  </ZTooltip>
                              ) : (
                                  button
                              );
                          })}
                    {customApps.map((app) => (
                        <ToolButton
                            key={`custom-app-${app.aiCustomAppID}`}
                            $active={selectedCustomAppId === app.aiCustomAppID}
                            $disabled={isNavigationDisabled}
                            onClick={isNavigationDisabled ? undefined : () => onCustomAppChange?.(app)}
                        >
                            {app.name}
                        </ToolButton>
                    ))}
                </ToolsCarouselWrapper>
            )}
            {!isIframeMode && onManageCustomApps && (
                <ZTooltip position='bottom' title={T._('Custom apps', 'Admin')} enterDelay={0} exitDelay={0} interactive={false}>
                    <ManageCustomAppsButton
                        $disabled={isNavigationDisabled}
                        onClick={isNavigationDisabled ? undefined : onManageCustomApps}
                        aria-label={T._('Custom apps', 'Admin')}
                    >
                        <Icon>
                            <ExtensionIcon />
                        </Icon>
                    </ManageCustomAppsButton>
                </ZTooltip>
            )}
            {onClose && (
                <CloseButton onClick={onClose} aria-label={T._('Close', 'Admin')}>
                    <Icon>
                        <CloseIcon />
                    </Icon>
                </CloseButton>
            )}
        </HeaderContainer>
    );
};

export default Header;
